import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { storage } from './storageAdapter';

/**
 * Admin "view as plan" preview — the shared contract between web and mobile.
 *
 * An admin can browse the whole user-facing app as if they were on the free or
 * paid plan, so paywalls and premium gating can be seen from the learner's side
 * WITHOUT changing their own account. The selection is forwarded as the
 * `X-View-As-Plan` header; the backend honours it for admins only, and it
 * changes what is DISPLAYED (chapter padlocks, is_paid, daily counters) — never
 * real access. See ApplicationController#effective_paid_tier?.
 *
 * The key, the header name and the accepted values live here so the two clients
 * cannot drift from each other or from the backend.
 */
export type ViewAsPlan = 'free' | 'paid';

export const VIEW_AS_PLAN_STORAGE_KEY = 'adrodd_admin_view_as_plan';
export const VIEW_AS_PLAN_HEADER = 'X-View-As-Plan';

/**
 * The admin's currently-selected preview plan, or null for "my actual plan".
 * Anything unrecognised reads as null rather than being forwarded, so a corrupt
 * value can never put the client in an unexplained state.
 */
export function readViewAsPlan(): ViewAsPlan | null {
  try {
    const raw = storage.getItem(VIEW_AS_PLAN_STORAGE_KEY);
    return raw === 'free' || raw === 'paid' ? raw : null;
  } catch {
    return null;
  }
}

/** Set (or clear, with null) the preview plan. */
export function setViewAsPlan(plan: ViewAsPlan | null): void {
  try {
    if (plan) storage.setItem(VIEW_AS_PLAN_STORAGE_KEY, plan);
    else storage.removeItem(VIEW_AS_PLAN_STORAGE_KEY);
  } catch {
    // Preview is a convenience; never let storage failure break the app.
  }
}

/**
 * Forward the selected preview plan on every request. No header is sent when
 * nothing is selected, so a non-previewing client is byte-identical to before.
 */
export function applyViewAsPlanInterceptor(client: AxiosInstance): void {
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const viewAs = readViewAsPlan();
    if (viewAs) {
      config.headers[VIEW_AS_PLAN_HEADER] = viewAs;
    }
    return config;
  });
}
